/**
 * ArchLang v0.10 — expression evaluator, slice 1 (P1b-2): scalar evaluation of
 * an `Expr` over a bound subject (an `ElementNode`).
 *
 * One expression grammar (design §2) feeds `where` predicates, calculated
 * columns, lens entries, and styles. This slice evaluates the CORE: sigiled
 * getters (`@field` / `@@aspect` / the model-derived builtins `@name` / `@type`
 * / `@connections`), literals, the comparison / arithmetic / boolean operators,
 * unary minus, and `this`. It is enough to drive `where (<expr>)` over the node
 * universe (wired in selector-eval.ts).
 *
 * Capability getters (`@ver.changes(last 90 days)`, `@tel.verified`) resolve
 * through the capability registry + injected providers (§5): a registered getter
 * with a provider yields its value; an absent capability (unregistered namespace,
 * unknown getter, or no provider wired) routes to a severity-carrying finding via
 * `ctx.capabilities.onAbsent` and yields `undefined` — never a throw.
 *
 * DEFERRED (throws {@link UnsupportedExprError} — never a silent wrong value):
 * functions (`colorize`/`heat`/`css` — render casts, evaluated in
 * `view-projection.ts`, not as scalars here), and the process/todo builtins
 * (`@todos` / `@dist` / `@reversible` / `@compensated`, `@kind`), which need the
 * process-model walk / subject sort.
 */

import type { Value } from "./types.js";
import { BUILTIN_GETTERS } from "./selector-ast.js";
import type { Expr, ExprGetter } from "./selector-ast.js";
import type { ElementNode, EvalContext } from "./selector-eval.js";
import { lifecycleStateAt, readDecorationPath } from "./decorations.js";
import { evalSelector } from "./selector-eval.js";
import { resolveCapabilityGetter } from "./capability-registry.js";
import { latencyBuiltinValue } from "./process-latency.js";
import { testBuiltinValue } from "./process-test-canon.js";
import { parseDuration } from "@archlang/calc";

/** A scalar expression value; `undefined` = unresolved (an absent getter). */
export type ExprValue = number | string | boolean | undefined;

/** The eval context also drives `exists`/aggregates (which evaluate selectors). */
export type ExprContext = EvalContext;

/** An expression node deferred to a later slice — thrown, not silently wrong. */
export class UnsupportedExprError extends Error {
  constructor(readonly node: Expr, detail?: string) {
    super(`expression node '${node.kind}'${detail ? ` (${detail})` : ""} is not evaluated in this slice`);
    this.name = "UnsupportedExprError";
  }
}

/** Truthiness for a `where` predicate: present + non-empty + non-zero + not false. */
export function isTruthy(v: ExprValue): boolean {
  return v !== undefined && v !== false && v !== 0 && v !== "";
}

/** Evaluate an expression against a bound subject element. */
export function evalExpression(expr: Expr, subject: ElementNode, ctx: ExprContext): ExprValue {
  switch (expr.kind) {
    case "literal":
      return valueToScalar(expr.value);
    case "this":
      return subject.key;
    case "getter":
      return evalGetter(expr, subject, ctx);
    case "unary":
      return evalUnary(expr, subject, ctx);
    case "binary":
      return evalBinary(expr, subject, ctx);
    case "exists":
      return evalExists(expr, subject, ctx);
    case "aggregate":
      return evalAggregate(expr, subject, ctx);
    default:
      // call (render functions) — deferred (P2a).
      throw new UnsupportedExprError(expr);
  }
}

/** `exists ( <selector> )` — evaluate the selector with `this` = subject; true iff non-empty. */
function evalExists(expr: Extract<Expr, { kind: "exists" }>, subject: ElementNode, ctx: ExprContext): boolean {
  const r = evalSelector(expr.selector, { ...ctx, subject });
  return r.sort === "node" ? r.nodes.size > 0 : r.edges.size > 0;
}

/**
 * `count ( <sel> )` (either sort), `count distinct @g of ( <sel> )`,
 * `sum|min|max @g of ( <sel> )` (getter forms, node-sort). The selector
 * evaluates with `this` = the current subject; getter forms read `@g` per node.
 */
function evalAggregate(expr: Extract<Expr, { kind: "aggregate" }>, subject: ElementNode, ctx: ExprContext): ExprValue {
  const childCtx: EvalContext = { ...ctx, subject };
  const r = evalSelector(expr.selector, childCtx);
  if (expr.op === "count") {
    return r.sort === "node" ? r.nodes.size : r.edges.size;
  }
  // count distinct / sum / min / max — a getter over a node-sort set.
  if (r.sort !== "node") throw new UnsupportedExprError(expr, `${expr.op} needs a node-sort selector`);
  if (!expr.getter) throw new UnsupportedExprError(expr, `${expr.op} needs a getter`);
  const nodes = [...r.nodes].map((k) => ctx.universe.byKey(k)).filter((n): n is ElementNode => !!n);
  const values = nodes.map((n) => evalExpression(expr.getter!, n, childCtx));
  if (expr.op === "count distinct") {
    return new Set(values.filter((v) => v !== undefined)).size;
  }
  const nums = values.map(toNumber).filter((x) => !Number.isNaN(x));
  if (nums.length === 0) return expr.op === "sum" ? 0 : undefined;
  if (expr.op === "sum") return nums.reduce((a, b) => a + b, 0);
  if (expr.op === "min") return Math.min(...nums);
  return Math.max(...nums);
}

function evalGetter(g: ExprGetter, subject: ElementNode, ctx: ExprContext): ExprValue {
  // Capability getters (`@ns.getter(args)`, §5) route through the registry +
  // injected providers. `handled` distinguishes a real capability (consumed here,
  // possibly an absent finding → undefined) from an ordinary `@` field getter
  // (`@repo.url`), which falls through to the field/builtin path below.
  if (g.sigil === "@") {
    const cap = resolveCapabilityGetter(g, { key: subject.key, filePath: subject.source?.file }, ctx.capabilities);
    if (cap.handled) return cap.value;
  }
  const key = g.path.join(".");
  if (g.sigil === "@@") {
    const vals = subject.aspects.get(key);
    if (!vals || vals.length === 0) return undefined;
    if (vals.length > 1) throw new UnsupportedExprError(g, "list-valued aspect getter");
    return valueToScalar(vals[0]!);
  }
  // `@` — a builtin (reserved namespace) or a field.
  // `estimatedLatency` is a DOTTED builtin (`@estimatedLatency.min`), so it is
  // matched on its head segment, not the whole single-segment path.
  if (g.path[0] === "estimatedLatency") {
    // A single sub-getter only (`@estimatedLatency.system`) — a deeper tail
    // (`@estimatedLatency.system.foo`) is not a getter → undefined, not the
    // sub-value silently (which would let a garbage expression evaluate).
    if (g.path.length > 2) return undefined;
    return latencyBuiltinValue(g.path[1], ctx.processLatency?.(subject.key));
  }
  // `@tested` / `@testCoverage` (process-tests P9 governance) — resolved via the
  // injected ProcessTestResolver (neutral undefined for a non-process subject or
  // when unwired), matched before the reserved-namespace branch below.
  if (g.path.length === 1 && (g.path[0] === "tested" || g.path[0] === "testCoverage")) {
    return testBuiltinValue(g.path[0]!, ctx.processTest?.(subject.key));
  }
  if (g.path.length === 1 && BUILTIN_GETTERS.has(g.path[0]!)) {
    return evalBuiltin(g.path[0]!, subject, ctx, g);
  }
  // DECORATIONS come BEFORE fields: `@name` resolves the declared vocabulary
  // first, which is exactly what the homonym rule promises (and why a field
  // sharing a decoration's name is reported as unreachable). Bare `@deco` is
  // truthy-if-present; `@deco.field` reads an argument.
  const decoHit = decorationGetter(g, subject, ctx);
  if (decoHit !== NOT_A_DECORATION) return decoHit;
  const f = subject.fields.get(key);
  return f ? valueToScalar(f) : undefined;
}

/** Sentinel so "the decoration exists but its arg is unset" (→ undefined) stays
 *  distinguishable from "this name is not a decoration" (→ fall through).
 *  `readDecorationPath` draws that line; this only carries it into `ExprValue`,
 *  which has no room for a third state of its own. */
const NOT_A_DECORATION = Symbol("not-a-decoration") as unknown as ExprValue;

function decorationGetter(g: ExprGetter, subject: ElementNode, ctx: ExprContext): ExprValue {
  // This axis finds the instance in a Map, the field/description axes scan a
  // list — that lookup is the only thing they differ by. The rule itself is
  // shared, so a bare positional binds the same field on every axis.
  const inst = g.path[0] ? subject.decorations?.get(g.path[0]) : undefined;
  // `ctx.universe` is read only once there IS an instance: hand-built eval
  // contexts in tests and in the field-cast path carry no universe at all.
  if (!inst) return NOT_A_DECORATION;
  const read = readDecorationPath(inst, ctx.universe?.decorations, g.path);
  if (!read.hit) return NOT_A_DECORATION;
  return read.value === undefined ? undefined : valueToScalar(read.value);
}

function evalBuiltin(name: string, subject: ElementNode, ctx: ExprContext, g: ExprGetter): ExprValue {
  switch (name) {
    case "name":
      return subject.path[subject.path.length - 1];
    case "type":
      return subject.typeName;
    case "connections":
      return connectionCount(subject, ctx);
    case "lifecycle":
      // Undecorated elements are live at every scrub point.
      return subject.lifecycle
        ? lifecycleStateAt(subject.lifecycle, ctx.scrubAt ?? ctx.universe.now)
        : "live";
    default:
      // todos / dist / reversible / compensated (node) + kind (edge) — deferred.
      throw new UnsupportedExprError(g, `builtin @${name}`);
  }
}

/** Degree of the subject in the derived graph (call edges touching it). */
function connectionCount(subject: ElementNode, ctx: ExprContext): number {
  if (!ctx.graph) throw new UnsupportedExprError({ kind: "getter", sigil: "@", path: ["connections"] }, "no graph");
  const key = subject.key;
  let n = 0;
  for (const e of ctx.graph.edges) {
    if (e.kind !== "call") continue;
    if (
      e.fromModulePath.join(".") === key ||
      e.toModulePath.join(".") === key ||
      e.toInterfacePath.join(".") === key
    ) {
      n++;
    }
  }
  return n;
}

function evalUnary(expr: Extract<Expr, { kind: "unary" }>, subject: ElementNode, ctx: ExprContext): ExprValue {
  const v = evalExpression(expr.operand, subject, ctx);
  if (expr.op === "not") return !isTruthy(v);
  // `-` — unary minus on a number.
  return -toNumber(v);
}

function evalBinary(expr: Extract<Expr, { kind: "binary" }>, subject: ElementNode, ctx: ExprContext): ExprValue {
  // Short-circuit boolean operators.
  if (expr.op === "and") return isTruthy(evalExpression(expr.left, subject, ctx)) && isTruthy(evalExpression(expr.right, subject, ctx));
  if (expr.op === "or") return isTruthy(evalExpression(expr.left, subject, ctx)) || isTruthy(evalExpression(expr.right, subject, ctx));

  const l = evalExpression(expr.left, subject, ctx);
  const r = evalExpression(expr.right, subject, ctx);
  switch (expr.op) {
    case "=":
      return looseEquals(l, r);
    case "!=":
      return !looseEquals(l, r);
    case "<":
      return durNum(l) < durNum(r);
    case "<=":
      return durNum(l) <= durNum(r);
    case ">":
      return durNum(l) > durNum(r);
    case ">=":
      return durNum(l) >= durNum(r);
    case "+":
      return toNumber(l) + toNumber(r);
    case "-":
      return toNumber(l) - toNumber(r);
    case "*":
      return toNumber(l) * toNumber(r);
    case "/":
      return toNumber(l) / toNumber(r);
    default:
      return undefined;
  }
}

/** `=` / `!=` compare by value; a number-vs-numeric-string (incl. a
 *  duration-shaped literal, so `@estimatedLatency = 200ms` works) compares
 *  numerically. */
function looseEquals(a: ExprValue, b: ExprValue): boolean {
  if (a === undefined || b === undefined) return a === b;
  if (typeof a === "number" || typeof b === "number") {
    const an = durNum(a);
    const bn = durNum(b);
    if (!Number.isNaN(an) && !Number.isNaN(bn)) return an === bn;
  }
  return String(a) === String(b);
}

function toNumber(v: ExprValue): number {
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v === "string") return Number(v);
  return NaN;
}

/** Number coercion for ordering comparisons that is **duration-aware**: a
 *  duration-shaped literal (`500ms`, `50ms-500ms`) coerces to seconds, so
 *  `@estimatedLatency > 500ms` and `@latency < 1s` compare on the time axis.
 *  Additive over {@link toNumber} — a plain number/numeric string is unchanged
 *  (a bare number is not a duration), so only previously-NaN strings shift. */
function durNum(v: ExprValue): number {
  if (typeof v === "string") {
    const d = parseDuration(v);
    if (d.ok) return d.hi; // a point literal has lo === hi; a range compares by its upper
  }
  return toNumber(v);
}

/** Coerce a model `Value` to a scalar (identifier → its text, list → undefined). */
function valueToScalar(v: Value): ExprValue {
  switch (v.kind) {
    case "string":
    case "identifier":
      return v.text;
    case "number":
      return v.value;
    case "boolean":
      return v.value;
    case "ref":
    case "list":
      return undefined; // no scalar form
    case "expr":
      // Unreachable once cascade has run (field-exprs resolves every getter and
      // cast first); listed rather than swallowed by a `default` so the compiler
      // points here if the value grammar grows again.
      return undefined;
  }
}
